import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, Form, Container, Alert, Spinner } from 'react-bootstrap';
import { BiCaretUp, BiCaretDown } from 'react-icons/bi';
import { useAuth } from '../context/AuthContext';
import { usePeriodBuffer } from '../context/PeriodBufferContext';
import axios from 'axios';
import SmartImage from '../components/SmartImage';
import KonvaBoxes from '../components/inventory/KonvaBoxes';
import KonvaBottles from '../components/inventory/KonvaBottles';
import KonvaSacks from '../components/inventory/KonvaSacks';

// -------------------- Helpers --------------------

const MIN_STEPS_PER_BOX = 10; // fallback only when measureIncrement missing
const WEIGHT_REGEX = /([\d.,]+)\s*([a-zA-Z]*)/;

function parseWeightToBaseUnits(raw) {
  if (!raw || typeof raw !== 'string') return { value: 0, unitLabel: 'units' };
  const m = raw.trim().toLowerCase().match(WEIGHT_REGEX);
  if (!m) return { value: 0, unitLabel: 'units' };

  const num = parseFloat(String(m[1]).replace(',', '.'));
  const unit = (m[2] || '').toLowerCase();

  if (unit.startsWith('kg')) return { value: Math.round(num * 1000), unitLabel: 'g' };
  if (unit === 'g' || unit.startsWith('gram')) return { value: Math.round(num), unitLabel: 'g' };
  if (unit === 'l' || unit === 'lt' || unit.startsWith('lit')) return { value: Math.round(num * 1000), unitLabel: 'ml' };
  if (unit === 'ml') return { value: Math.round(num), unitLabel: 'ml' };

  if (raw.toLowerCase().includes('ml')) return { value: Math.round(num), unitLabel: 'ml' };
  if (raw.toLowerCase().includes('g')) return { value: Math.round(num), unitLabel: 'g' };

  return { value: Math.round(num), unitLabel: 'units' };
}

function parseFlexibleNumber(raw) {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (raw == null) return 0;
  let s = String(raw).trim().replace(/\s+/g, '');
  if (s.includes(',') && s.includes('.')) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (s.includes(',')) {
    s = s.replace(',', '.');
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function normalizeUnit(u) {
  const s = String(u || '').trim().toLowerCase();
  if (['kilogram', 'kilograms', 'kg'].includes(s)) return 'kg';
  if (['gram', 'grams', 'g'].includes(s)) return 'g';
  if (['liter', 'litre', 'liters', 'litres', 'l'].includes(s)) return 'l';
  if (['milliliter', 'millilitre', 'milliliters', 'millilitres', 'ml'].includes(s)) return 'ml';
  if (['unit', 'units', 'each'].includes(s)) return 'units';
  return s || 'units';
}

function convertUnits(value, fromUnit, toUnit) {
  const fromU = normalizeUnit(fromUnit);
  const toU = normalizeUnit(toUnit);
  if (fromU === toU) return value;

  if (fromU === 'kg' && toU === 'g') return value * 1000;
  if (fromU === 'g' && toU === 'kg') return value / 1000;

  if (fromU === 'l' && toU === 'ml') return value * 1000;
  if (fromU === 'ml' && toU === 'l') return value / 1000;

  return value;
}

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const safeId = (obj) => obj?.id ?? obj?._id ?? null;

// -------------------- Component --------------------

const InventoryDetail = ({ setActiveView }) => {
  const [productName, setProductName] = useState('');
  const [productImageURL, setProductImageURL] = useState('');

  const [showCount, setShowCount] = useState(false);
  const [showBox, setShowBox] = useState(false);

  const [stockCount, setStockCount] = useState(1);
  const [measureIncrement, setMeasureIncrement] = useState(0);
  const [measureMaxVal, setMeasureMaxVal] = useState(0);
  const [unitLabel, setUnitLabel] = useState('ml'); // 'ml' | 'g' | 'units'
  const [containerBoxes, setContainerBoxes] = useState([]);
  const [boxCount, setBoxCount] = useState(0);

  const [lastStockTake, setLastStockTake] = useState(null);
  const [inventoryPeriodDays, setInventoryPeriodDays] = useState(0);
  const [, setInventoryPeriodId] = useState(null);
  const [periodBufferHours, setPeriodBufferHours] = useState(0);
  const [isWithinPeriod, setIsWithinPeriod] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  const [fallbackUsed, setFallbackUsed] = useState(false);
  const [fallbackText, setFallbackText] = useState('');

  // NEW: store for audit
  const [uomIdState, setUomIdState] = useState(null);
  const [uomLabelState, setUomLabelState] = useState(null);
  const [containerTypeIdState, setContainerTypeIdState] = useState(null);
  const [containerTypeDescState, setContainerTypeDescState] = useState(null);

  // NEW: admin override state
  const [isOverride, setIsOverride] = useState(false);

  // Master Admin (userGroup 0) and Tenant Admin (userGroup 1) can override
  // the stock-take lock. Tenant isolation is enforced server-side, so a
  // Tenant Admin can only ever see (and therefore override) their own
  // tenant's products.
  const { isAuthenticated, verifySession, logout, user } = useAuth();
  const { periodBuffers } = usePeriodBuffer();
  const userGroupFromLocal =
    (() => {
      try {
        const u = JSON.parse(localStorage.getItem('user') || 'null');
        return u?.userGroup ?? null;
      } catch { return null; }
    })();
  const effectiveUserGroup = user?.userGroup ?? user?.group ?? userGroupFromLocal ?? null;
  const isAdmin = Number(effectiveUserGroup) === 0 || Number(effectiveUserGroup) === 1;

  const { productUuid } = useParams();

  const [isBottle, setIsBottle] = useState(false);
  const [isSack, setIsSack] = useState(false);
  const [forceBoxLike, setForceBoxLike] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const returnCategory = location.state?.returnCategory ?? 'ALL_DUE';

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentDateTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Steps per box = max / measureIncrement (rounded up)
  const stepsPerBox = useMemo(() => {
    const inc = measureIncrement > 0 ? measureIncrement : 1;
    const max = measureMaxVal > 0 ? measureMaxVal : inc;
    return Math.max(1, Math.ceil(max / inc));
  }, [measureIncrement, measureMaxVal]);

  // Fetch + prefill
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        if (!isAuthenticated || !productUuid) return;
        if (!cancelled) {
          setLoading(true);
          setError('');
          setSuccess('');
          setFallbackUsed(false);
          setFallbackText('');
          setIsOverride(false); // reset override when loading a new product
        }

        await verifySession();

        // 1) Product
        const prodRes = await axios.post('/products/by-id', { id: productUuid }, { withCredentials: true });
        const prod = prodRes?.data ?? {};
        const productImg = prod?.imageURL || '/placeholder-product.png';
        const { value: weightValue, unitLabel: parsedUnit } = parseWeightToBaseUnits(prod?.metadata?.weight || '');

        // 2) UOM / Container Type
        const uomRes = await axios.get(`/uom-inventory/by-product/${productUuid}`, { withCredentials: true });
        const uomList = uomRes?.data?.data ?? uomRes?.data ?? [];
        const hasUom = Array.isArray(uomList) && uomList.length > 0;

        let nextProductName = prod?.name || 'Product';
        let nextProductImageURL = productImg;

        let nextShowCount = false;
        let nextShowBox = false;
        let nextStockCount = 1;

        let nextMeasureInc = 0;
        let nextMeasureMax = 0;
        let nextBoxCount = 0;
        let nextUnitLabel = parsedUnit || 'ml';
        let nextIsBottle = false;
        let nextIsSack = false;
        let nextForceBoxLike = false;

        let localFallbackUsed = false;
        let localFallbackText = '';

        const hasWord = (rx, ...vals) =>
          vals.some(v => rx.test(String(v || '')));

        const endsWithSingleG = (s) =>
          /(?:^|[^A-Za-z])g$/i.test(String(s || '').trim());

        if (hasUom) {
          const inv = uomList[0];
          const uomId = inv?.uomId;
          const uomLabel = String(inv?.uomFriendlyName || inv?.uomName || inv?.uomLabel || inv?.uomDescription || '');
          const maxQuantity = inv?.maxQuantity;
          const containerTypeId = inv?.containerTypeId;

          // keep for audit later
          setUomIdState(uomId || null);
          setUomLabelState(uomLabel || null);

          if (uomId === '04e88a70-8929-4948-8632-2859dc984cec') {
            // Each
            nextShowCount = true;
            nextShowBox = false;
            nextStockCount = Math.max(1, Math.floor(Number(maxQuantity) || 1));
            nextUnitLabel = 'units';
          } else if (containerTypeId) {
            const ctRes = await axios.get(`/container-types/${containerTypeId}`, { withCredentials: true });
            const ct = ctRes?.data?.data ?? ctRes?.data ?? {};
            const ctName = String(ct?.containerTypeName || '');
            const ctFriendly = String(ct?.friendlyName || '').trim();

            // Store container type info for audit
            setContainerTypeIdState(containerTypeId || null);
            setContainerTypeDescState(ctName || null);

            const isGramCT = endsWithSingleG(ctFriendly);
            nextForceBoxLike = isGramCT || hasWord(/box|pack/i, ctName, ctFriendly, uomLabel);

            nextIsBottle = !nextForceBoxLike && hasWord(/bottle/i, ctName, ctFriendly, uomLabel);
            nextIsSack   = !nextForceBoxLike && hasWord(/sack/i, ctName, ctFriendly, uomLabel);

            if (nextForceBoxLike || nextIsBottle || nextIsSack) {
              nextShowCount = false;
              nextShowBox = true;

              const productBasedMax = Number.isFinite(weightValue) && weightValue > 0 ? weightValue : 0;
              let maxNum = 0;

              if (productBasedMax > 0) {
                maxNum = productBasedMax;
                nextUnitLabel = isGramCT ? 'g' : (parsedUnit || 'g');
              } else if (Number(ct?.measureMax) > 0) {
                maxNum = Number(ct.measureMax);
                nextUnitLabel = isGramCT
                  ? 'g'
                  : (parsedUnit && parsedUnit !== 'units' ? parsedUnit : 'g');
                localFallbackUsed = true;
                localFallbackText = `No product weight provided; using container type capacity (${maxNum}${nextUnitLabel}).`;
              } else {
                maxNum = 500;
                nextUnitLabel = isGramCT
                  ? 'g'
                  : (parsedUnit && parsedUnit !== 'units' ? parsedUnit : 'g');
                localFallbackUsed = true;
                localFallbackText = `No product weight provided; using default capacity (${maxNum}${nextUnitLabel}).`;
              }

              const ctInc = Number(ct?.measureIncrement) > 0 ? Number(ct.measureIncrement) : 0;
              const fallbackInc = Math.max(1, Math.floor(maxNum / MIN_STEPS_PER_BOX));
              const uiInc = ctInc > 0 ? ctInc : fallbackInc;

              nextMeasureMax = maxNum;
              nextMeasureInc = uiInc;
              nextBoxCount = Math.max(1, Math.floor(Number(maxQuantity) || 1));
            } else {
              // Container type exists but doesn't match box/bottle/sack — fall back to a simple count
              nextShowBox = false;
              nextShowCount = true;
              nextStockCount = Math.max(1, Math.floor(Number(maxQuantity) || 1));
              nextUnitLabel = uomLabel || (parsedUnit && parsedUnit !== 'units' ? parsedUnit : 'units');
            }
          } else {
            const boxishFromUom = /box|pack/i.test(uomLabel);
            nextIsSack = /sack/i.test(uomLabel);

            if (boxishFromUom || nextIsSack) {
              nextShowBox = true;

              const productBasedMax = Number.isFinite(weightValue) && weightValue > 0 ? weightValue : 0;
              const maxNum = productBasedMax > 0 ? productBasedMax : 500;

              nextMeasureMax = maxNum;
              nextMeasureInc = Math.max(1, Math.floor(maxNum / MIN_STEPS_PER_BOX));
              nextBoxCount = Math.max(1, Math.floor(Number(inv?.maxQuantity) || 1));
              nextUnitLabel = parsedUnit && parsedUnit !== 'units' ? parsedUnit : 'g';

              localFallbackUsed = productBasedMax === 0;
              if (localFallbackUsed) {
                localFallbackText = `Using default capacity (${maxNum}${nextUnitLabel}).`;
              }
            } else {
              // UOM exists but label doesn't match box/pack/sack — fall back to a simple count
              nextShowBox = false;
              nextShowCount = true;
              nextStockCount = Math.max(1, Math.floor(Number(inv?.maxQuantity) || 1));
              nextUnitLabel = uomLabel || (parsedUnit && parsedUnit !== 'units' ? parsedUnit : 'units');
            }
          }
        }

        // 3) Product-Inventory (period + prefill)
        let invData = null;
        try {
          const piRes = await axios.get(`/product-inventory/product/${productUuid}`, { withCredentials: true });
          invData = piRes?.data?.data ?? null;
        } catch (e) {
          if (e?.response?.status !== 404) throw e;
        }

        // Prefill Stock Take Count for "Each" (units) from latest inventory response
        if (nextShowCount && !nextShowBox) {
          let storedCount = invData
            ? parseFlexibleNumber(
                typeof invData.quantity !== 'undefined' && invData.quantity !== null
                  ? invData.quantity
                  : invData.stockTakeCount
              )
            : 0;

          const storedUnit =
            invData && typeof invData.quantity === 'undefined' ? invData.stockTakeUnit : undefined;

          if (storedUnit) storedCount = convertUnits(storedCount, storedUnit, nextUnitLabel);

          nextStockCount = Math.max(0, Math.round(storedCount));
        }

        let nextLastTake = null;
        let nextPeriodDays = 0;
        let nextPeriodId = null;
        let nextBufferHours = 0;
        let nextIsWithinPeriod = false;

        if (invData) {
          nextLastTake = invData.lastStockTake ? new Date(invData.lastStockTake) : null;
          nextPeriodDays = typeof invData.inventoryPeriod === 'number' ? invData.inventoryPeriod : 0;

          // Get period buffer from context
          if (nextPeriodDays > 0) {
            try {
              const periodsRes = await axios.get('/inventory-periods', { withCredentials: true });
              const periods = periodsRes.data?.data || periodsRes.data || [];
              const period = periods.find(p => p.inventoryDays === nextPeriodDays);

              if (period) {
                nextPeriodId = period.id;
                // Get buffer from context (already loaded at app startup)
                nextBufferHours = periodBuffers.get(period.id) || 0;
              }
            } catch (err) {
              console.warn('Failed to fetch inventory periods:', err);
            }
          }

          // Calculate if within period, accounting for buffer
          if (nextLastTake && Number.isFinite(nextPeriodDays) && nextPeriodDays > 0) {
            const periodInMs = nextPeriodDays * 24 * 60 * 60 * 1000;
            const bufferInMs = nextBufferHours * 60 * 60 * 1000;
            const adjustedPeriodInMs = periodInMs - bufferInMs;
            const timeSinceLastTake = Date.now() - nextLastTake.getTime();
            nextIsWithinPeriod = timeSinceLastTake < adjustedPeriodInMs;
          }
        }

        // Prefill containers from stored quantity
        let prefilledBoxes = [];
        if (nextShowBox && nextBoxCount > 0) {
          const inc = nextMeasureInc > 0 ? nextMeasureInc : 1;
          const maxPerBox = nextMeasureMax > 0 ? nextMeasureMax : inc;
          const maxSteps = Math.max(1, Math.ceil(maxPerBox / inc));
          const containers = nextBoxCount;

          let storedCount = invData
            ? parseFlexibleNumber(
                typeof invData.quantity !== 'undefined' && invData.quantity !== null
                  ? invData.quantity
                  : invData.stockTakeCount
              )
            : 0;

          const storedUnit =
            invData && typeof invData.quantity === 'undefined' ? invData.stockTakeUnit : undefined;

          if (storedUnit) storedCount = convertUnits(storedCount, storedUnit, nextUnitLabel);

          let remaining = clamp(storedCount, 0, maxPerBox * containers);
          for (let i = 0; i < containers; i++) {
            const stepsForThis = Math.min(Math.floor(remaining / inc), maxSteps);
            prefilledBoxes.push(stepsForThis);
            remaining -= stepsForThis * inc;
          }
        }

        if (!cancelled) {
          setProductName(nextProductName);
          setProductImageURL(nextProductImageURL);

          setShowCount(nextShowCount);
          setShowBox(nextShowBox);
          setStockCount(nextStockCount);

          setMeasureIncrement(nextMeasureInc);
          setMeasureMaxVal(nextMeasureMax);
          setBoxCount(nextBoxCount);

          setUnitLabel(nextUnitLabel);
          setContainerBoxes(nextShowBox ? (prefilledBoxes.length ? prefilledBoxes : Array.from({ length: nextBoxCount }, () => 0)) : []);

          setFallbackUsed(localFallbackUsed);
          setFallbackText(localFallbackText);

          setLastStockTake(nextLastTake);
          setInventoryPeriodDays(nextPeriodDays);
          setInventoryPeriodId(nextPeriodId);
          setPeriodBufferHours(nextBufferHours);
          setIsWithinPeriod(nextIsWithinPeriod);

          setIsBottle(nextIsBottle);
          setIsSack(nextIsSack);
          setForceBoxLike(nextForceBoxLike);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError(err.response?.data?.message || 'Failed to load inventory detail');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [isAuthenticated, productUuid, verifySession, periodBuffers]);

  // -------------------- Handlers --------------------

  const handleCountChange = (e) =>
    setStockCount(() => Math.max(parseInt(e.target.value, 10) || 1, 1));
  const incCount = () => setStockCount((c) => c + 1);
  const decCount = () => setStockCount((c) => (c > 0 ? c - 1 : 0));

  // computed lock state: locked if within period AND override not enabled
  const lockEditing = isWithinPeriod && !isOverride;

  // For containers: one click == ONE BACKEND INCREMENT worth of fill
  const incBox = (idx) => {
    if (lockEditing) return;
    const maxSteps = stepsPerBox;
    setContainerBoxes((boxes) =>
      boxes.map((f, i) => (i === idx ? Math.min(f + 1, maxSteps) : f))
    );
  };
  const decBox = (idx) => {
    if (lockEditing) return;
    setContainerBoxes((boxes) =>
      boxes.map((f, i) => (i === idx ? Math.max(f - 1, 0) : f))
    );
  };

  // Total quantity
  const computedQuantity = useMemo(() => {
    if (showCount) return Number.isFinite(stockCount) ? stockCount : 0;
    if (showBox && containerBoxes.length) {
      const inc = measureIncrement > 0 ? measureIncrement : 1;
      return containerBoxes.reduce((sum, steps) => sum + steps * inc, 0);
    }
    return 0;
  }, [showCount, showBox, containerBoxes, stockCount, measureIncrement]);

  // Persist then navigate back (WITH AUDIT)
  const handleUpdateStock = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await verifySession();

      // 1) Update product-inventory with latest values
      const nowISO = new Date().toISOString();
      await axios.patch(
        `/product-inventory/product/${productUuid}`,
        {
          lastStockTake: nowISO,
          quantity: computedQuantity,
        },
        { withCredentials: true }
      );

      // 2) Re-fetch updated product-inventory (source of truth for audit)
      const [invLatestRes, uomInvRes] = await Promise.all([
        axios.get(`/product-inventory/product/${productUuid}`, { withCredentials: true }),
        axios.get(`/uom-inventory/by-product/${productUuid}`, { withCredentials: true }),
      ]);

      const invLatest = invLatestRes?.data?.data ?? {};
      const uomListNow = uomInvRes?.data?.data ?? uomInvRes?.data ?? [];
      const firstUom = Array.isArray(uomListNow) && uomListNow.length ? uomListNow[0] : null;

      // Determine uomId / containerTypeId (prefer state; fall back to fresh read)
      const uomId = uomIdState || firstUom?.uomId || null;
      const containerTypeId = containerTypeIdState || firstUom?.containerTypeId || null;

      // 3) Fetch uom label and container type info for warnings/desc
      let uomLabel = uomLabelState;
      if (!uomLabel && uomId) {
        try {
          const r = await axios.get(`/uoms/${uomId}`, { withCredentials: true });
          uomLabel = r?.data?.data?.label ?? r?.data?.label ?? null;
        } catch {
          uomLabel = null;
        }
      }

      let containerTypeDesc = containerTypeDescState;
      let ctChosen = null;
      
      if (containerTypeId) {
        try {
          const r = await axios.get(`/container-types/${containerTypeId}`, { withCredentials: true });
          ctChosen = r?.data?.data ?? r?.data ?? null;
          containerTypeDesc = ctChosen?.containerTypeName || containerTypeDesc;
        } catch { /* ignore */ }
      }

      // If we still don't have container info, try to get it from UOM
      if (!ctChosen && uomId) {
        try {
          const r = await axios.get(`/container-types/uom/${uomId}`, { withCredentials: true });
          const ctByUom = r?.data?.data ?? r?.data ?? null;
          if (Array.isArray(ctByUom)) {
            ctChosen = ctByUom.find(ct => (safeId(ct) === containerTypeId)) || ctByUom[0] || null;
          } else if (ctByUom && typeof ctByUom === 'object') {
            ctChosen = ctByUom;
          }
          if (ctChosen && !containerTypeDesc) {
            containerTypeDesc = ctChosen.containerTypeName || '';
          }
        } catch { /* ignore */ }
      }

      const lowLevelWarning1Enable = Boolean(ctChosen?.lowLevelWarning1Enable);
      const lowLevelWarning2Enable = Boolean(ctChosen?.lowLevelWarning2Enable);
      const lowLevelWarning3Enable = Boolean(ctChosen?.lowLevelWarning3Enable);

      const lowLevel1Warning = ctChosen?.lowLevel1Warning != null ? parseFlexibleNumber(ctChosen.lowLevel1Warning) : null;
      const lowLevel2Warning = ctChosen?.lowLevel2Warning != null ? parseFlexibleNumber(ctChosen.lowLevel2Warning) : null;
      const lowLevel3Warning = ctChosen?.lowLevel3Warning != null ? parseFlexibleNumber(ctChosen.lowLevel3Warning) : null;

      // 4) Build audit payload with new fields
      const auditPayload = {
        inventory_id: safeId(invLatest) || `temp-${Date.now()}`,
        product_id: invLatest?.productUuid ?? productUuid,
        product_name: (productName || '').toString().trim(),
        uomId: uomId,
        uomLabel: uomLabel || 'Unknown',
        containerTypeId: containerTypeId,
        containerTypeDesc: containerTypeDesc || 'Unknown',
        lowLevelWarning1Enable,
        lowLevelWarning2Enable,
        lowLevelWarning3Enable,
        lowLevel1Warning,
        lowLevel2Warning,
        lowLevel3Warning,
        lastStockTake: invLatest?.lastStockTake ?? nowISO,
        quantity: invLatest?.quantity ?? computedQuantity,
        inventoryPeriod: invLatest?.inventoryPeriod ?? 0,
        createdBy: user?.id || user?._id || 'unknown-user',
        action_type: isOverride ? 'ADJUST' : 'UPDATE',
        changed_by: user?.id || user?._id || 'unknown-user',
        change_reason: isOverride ? 'Admin override stock adjustment' : 'Regular stock take update'
      };

      // 5) POST to /audit-inventory
      await axios.post('/audit-inventory', auditPayload, { withCredentials: true });

      setSuccess('Stock take updated and audit recorded successfully!');
      setActiveView('inventory');
      navigate('/dashboard/inventory', {
        state: { flash: 'Stock updated and audit recorded.', category: returnCategory }
      });
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) logout();
      else setError(err.response?.data?.message || 'Update or audit write failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setActiveView('inventory');
    // Navigate back to the category the user was viewing
    navigate('/dashboard/inventory', { state: { category: returnCategory } });
  };

  // NEW: enable override
  const handleEnableOverride = () => {
    if (isAdmin && isWithinPeriod) {
      setIsOverride(true);
      setSuccess('Admin override enabled for this stock take.');
    }
  };

  // -------------------- Render --------------------

  if (!isAuthenticated) return null;
  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" />
      </Container>
    );
  }

  const containerLabel = isBottle ? 'bottle' : isSack ? 'sack' : 'box';

  return (
    <Container className="content-container">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      {success && <Alert variant="success" className="mb-3">{success}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button variant="outline-secondary" onClick={handleBack}>
          ← Back to Inventory
        </Button>
        <h1 className="m-0">{productName}</h1>
        <div className="d-flex align-items-center gap-3">
          {showBox && (
            <span className="text-muted small">
              Total: <strong>{computedQuantity.toFixed(0)}{unitLabel}</strong>
            </span>
          )}

          {/* NEW: Inventory Overwrite button (to the LEFT of Update Stock) */}
          {isWithinPeriod && isAdmin && !isOverride && (
            <Button
              variant="warning"
              onClick={handleEnableOverride}
              title="Allow a stock take despite the current 7-day lock (admin only)"
            >
              Inventory Overwrite
            </Button>
          )}

          <Button
            variant="primary"
            onClick={handleUpdateStock}
            disabled={loading || lockEditing}
            title={lockEditing ? 'Not eligible for a new stock take yet' : 'Update Stock'}
          >
            Update Stock
          </Button>
        </div>
      </div>

      <div className="text-center mb-2">
        <SmartImage
          src={productImageURL}
          alt={productName}
          placeholder="/placeholder-product.png"
          style={{ maxHeight: '300px' }}
          className="img-fluid"
        />
      </div>

      {showBox && fallbackUsed && (
        <div className="text-muted small mb-3" aria-live="polite">
          {fallbackText}
        </div>
      )}

      <Form>
        <Form.Group className="mb-3">
          <Form.Label>Stock Take Date and Time</Form.Label>
          <Form.Control
            plaintext
            readOnly
            style={lockEditing ? { color: 'red' } : {}}
            value={
              (isWithinPeriod && !isOverride && lastStockTake)
                ? lastStockTake.toLocaleString()
                : currentDateTime.toLocaleString()
            }
          />
          {isWithinPeriod && (
            <div style={{ color: 'red', marginTop: '0.5rem' }}>
              The last stock take occurred less than {inventoryPeriodDays} days
              {periodBufferHours > 0 && ` (minus ${periodBufferHours} hour buffer)`} ago. Editing is disabled.
              {isAdmin && isOverride && (
                <span style={{ color: '#6c757d', marginLeft: '0.5rem' }}>
                  (Admin override active)
                </span>
              )}
            </div>
          )}
        </Form.Group>

        {showCount && !showBox && (
          <Form.Group className="mb-3 d-flex align-items-center">
            <Form.Label className="me-2 mb-0">Stock Take Count ({unitLabel})</Form.Label>
            <Form.Control
              type="text"
              value={stockCount}
              onChange={handleCountChange}
              disabled={lockEditing}
              style={{ width: '80px', marginRight: '10px' }}
            />
            <Button
              variant="outline-primary"
              className="me-2"
              onClick={incCount}
              disabled={lockEditing}
            >
              <BiCaretUp className="me-1" /> Increase
            </Button>
            <Button
              variant="outline-secondary"
              onClick={decCount}
              disabled={lockEditing}
            >
              <BiCaretDown className="me-1" /> Decrease
            </Button>
          </Form.Group>
        )}

        {showBox && boxCount > 0 && containerBoxes.length > 0 && (
          <>
            <div className="mb-2" style={{ fontStyle: 'italic' }}>
              Each {containerLabel} capacity: {measureMaxVal}{unitLabel} • Step: {measureIncrement}{unitLabel}
            </div>

            {forceBoxLike ? (
              <KonvaBoxes
                boxes={containerBoxes}
                stepsPerBox={stepsPerBox}
                unitLabel={unitLabel}
                measureIncrement={measureIncrement}
                measureMaxVal={measureMaxVal}
                actionable={!lockEditing}
                onInc={incBox}
                onDec={decBox}
              />
            ) : isBottle ? (
              <KonvaBottles
                boxes={containerBoxes}
                stepsPerBox={stepsPerBox}
                unitLabel={unitLabel}
                measureIncrement={measureIncrement}
                measureMaxVal={measureMaxVal}
                actionable={!lockEditing}
                onInc={incBox}
                onDec={decBox}
              />
            ) : isSack ? (
              <KonvaSacks
                boxes={containerBoxes}
                stepsPerBox={stepsPerBox}
                unitLabel={unitLabel}
                measureIncrement={measureIncrement}
                measureMaxVal={measureMaxVal}
                actionable={!lockEditing}
                onInc={incBox}
                onDec={decBox}
              />
            ) : (
              <KonvaBoxes
                boxes={containerBoxes}
                stepsPerBox={stepsPerBox}
                unitLabel={unitLabel}
                measureIncrement={measureIncrement}
                measureMaxVal={measureMaxVal}
                actionable={!lockEditing}
                onInc={incBox}
                onDec={decBox}
              />
            )}

            <div className="mt-3 d-flex flex-wrap gap-3">
              {containerBoxes.map((steps, idx) => {
                const inc = measureIncrement > 0 ? measureIncrement : 1;
                const filledAmount = steps * inc;
                const label = forceBoxLike ? 'Box' : (isBottle ? 'Bottle' : isSack ? 'Sack' : 'Box');
                return (
                  <div key={idx} className="d-flex align-items-center">
                    <span style={{ minWidth: 140 }}>
                      {label} {idx + 1}: {filledAmount.toFixed(0)}{unitLabel}
                    </span>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-2"
                      onClick={() => incBox(idx)}
                      disabled={lockEditing}
                    >
                      <BiCaretUp />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => decBox(idx)}
                      disabled={lockEditing}
                    >
                      <BiCaretDown />
                    </Button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Form>
    </Container>
  );
};

export default InventoryDetail;